import {
  createContext,
  useContext,
  useMemo,
  ReactNode,
} from 'react';

import { useAuth } from 'context/AuthContext';
import { useGetRegistryById } from 'hooks/useRegistries';

/* =========================
   Types
========================= */

type RegistryContextType = {
  registry: any;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

type RegistryProviderProps = {
  registryId: number;
  children: ReactNode;
};

/* =========================
   Context
========================= */

const RegistryContext = createContext<RegistryContextType | undefined>(
  undefined
);

/* =========================
   Provider
========================= */

export function RegistryProvider({ registryId, children }: RegistryProviderProps) {
  const {
    data: registry,
    refetch,
    isLoading,
    isFetching,
  } = useGetRegistryById(registryId, {
    enabled: !!registryId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 60_000,
    select: (res: any) => {
      return res.data;
    },
  });

  const value = useMemo(
    () => ({ registry, isLoading, isFetching, refetch }),
    [registry, isLoading, isFetching, refetch]
  );

  return <RegistryContext.Provider value={value}>{children}</RegistryContext.Provider>;
}

/* =========================
   Hook
========================= */

export function useRegistryContext() {
  const context = useContext(RegistryContext);

  if (!context) {
    throw new Error(
      'useRegistryContext must be used within a RegistryProvider'
    );
  }

  return context;
}
